"use client";

import { useState } from "react";

import { useInvalidateServerComponent } from "@/src/components/server/client-provider";
import { Button } from "@/src/components/shadcn/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/src/components/shadcn/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/src/components/shadcn/ui/select";
import { ValerieCard } from "@/src/components/valerie-card";
import {
  useAthenaCoverageResolution,
  useResolveAthenaCoverage,
} from "@/src/lib/data-fetchers/athena-coverage-resolution-fetchers";
import type { AthenaCoverageChoice, AthenaCoverageResolutionPreview } from "@/src/models/athena-coverage-resolution";
import { getSubscriberName } from "@/src/models/coverage";

function CoverageResolutionForm({
  mutation,
  preview,
  onComplete,
}: {
  mutation: ReturnType<typeof useResolveAthenaCoverage>;
  preview: AthenaCoverageResolutionPreview;
  onComplete: () => void;
}) {
  const [choices, setChoices] = useState<AthenaCoverageChoice[]>([{ policyKey: "" }]);
  const acceptSource = (source: "valerie" | "athena") => {
    const candidates = preview.candidates
      .filter((candidate) => candidate.source === source)
      .toSorted((left, right) => (left.order ?? 3) - (right.order ?? 3));

    setChoices(candidates.map(({ key }) => ({ policyKey: key })));
    mutation.reset();
  };

  const updateChoice = (index: number, choice: AthenaCoverageChoice) => {
    setChoices(choices.map((current, row) => (row === index ? choice : current)));
    mutation.reset();
  };

  const handleSubmit = () => mutation.mutate({ version: preview.version, choices }, { onSuccess: onComplete });

  const incomplete = choices.some(
    (choice) =>
      !choice.policyKey || !preview.candidates.find((candidate) => candidate.key === choice.policyKey)?.packageId
  );

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        {(["valerie", "athena"] as const).map((source) => {
          const candidates = preview.candidates.filter((candidate) => candidate.source === source);

          return (
            <ValerieCard key={source} title={source === "valerie" ? "Valerie" : "Athena"} contentClassName="space-y-2">
              {candidates.length === 0 && <p className="text-sm text-muted-foreground">No coverage</p>}
              {candidates.map((candidate) => (
                <div key={candidate.key} className="text-sm">
                  <p>
                    {candidate.label}{" "}
                    {candidate.casePolicy
                      ? "(case policy)"
                      : candidate.order === 1
                        ? "(primary)"
                        : candidate.order === 2
                          ? "(secondary)"
                          : `(order ${candidate.order ?? "unknown"})`}
                  </p>
                  <p className="text-muted-foreground">
                    Package: {candidate.packageId ?? "Unresolved"} · Member: {candidate.memberId ?? "None"}
                  </p>
                  <p className="text-muted-foreground">
                    {getSubscriberName(candidate) ?? "Subscriber name missing"} ·{" "}
                    {candidate.subscriberDateOfBirth ?? "DOB missing"} · {candidate.subscriberSex ?? "Sex missing"}
                  </p>
                  <p className="text-muted-foreground">
                    Relationship: {candidate.relationshipToSubscriber ?? "None"} · Group:{" "}
                    {candidate.groupNumber ?? "None"}
                  </p>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={
                  mutation.isPending ||
                  candidates.length === 0 ||
                  candidates.length > 2 ||
                  (candidates.length > 1 && candidates.some((candidate) => candidate.selfPay)) ||
                  candidates.some((candidate) => !candidate.packageId)
                }
                onClick={() => acceptSource(source)}
              >
                Accept {source === "valerie" ? "VH" : "Athena"}
              </Button>
              {candidates.length > 2 && (
                <p className="text-xs text-muted-foreground">Choose the applicable policies below.</p>
              )}
            </ValerieCard>
          );
        })}
      </div>
      <p className="text-sm text-muted-foreground">
        Choose Valerie or Athena for each primary and secondary policy. Each selection includes all of that policy's
        subscriber details.
      </p>
      {preview.candidates.some((candidate) => !candidate.packageId) && (
        <p className="text-sm text-muted-foreground">
          An unresolved Valerie plan cannot be written yet. Choose the correct Athena policy, or correct it in Athena
          and refresh.
        </p>
      )}
      {choices.map((choice, index) => {
        const policy = preview.candidates.find((candidate) => candidate.key === choice.policyKey);

        return (
          <div key={index} className="space-y-2 rounded-md border p-3">
            <p className="font-medium">{index === 0 ? "Primary" : "Secondary"}</p>
            <Select
              value={choice.policyKey}
              disabled={mutation.isPending}
              onValueChange={(key) => updateChoice(index, { policyKey: key })}
            >
              <SelectTrigger className="bg-background" aria-label={`${index === 0 ? "Primary" : "Secondary"} policy`}>
                <SelectValue placeholder="Choose policy" />
              </SelectTrigger>
              <SelectContent>
                {preview.candidates.map((candidate) => (
                  <SelectItem
                    key={candidate.key}
                    value={candidate.key}
                    disabled={!candidate.packageId || (candidate.selfPay && choices.length > 1)}
                  >
                    {candidate.source === "valerie" ? "VH" : "Athena"}: {candidate.label} · Package{" "}
                    {candidate.packageId ?? "unresolved"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {policy && (
              <ValerieCard title="Selected coverage" contentClassName="space-y-1 text-sm">
                <p>
                  {policy.label} · Package: {policy.packageId ?? "Unresolved"}
                </p>
                <p>
                  Member: {policy.memberId ?? "None"} · Group: {policy.groupNumber ?? "None"}
                </p>
                <p>Subscriber: {getSubscriberName(policy) ?? "Name missing"}</p>
                <p>
                  DOB: {policy.subscriberDateOfBirth ?? "Missing"} · Sex: {policy.subscriberSex ?? "Missing"}
                </p>
                <p>Relationship: {policy.relationshipToSubscriber ?? "None"}</p>
              </ValerieCard>
            )}
            {index === 1 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={mutation.isPending}
                onClick={() => setChoices(choices.slice(0, 1))}
              >
                Remove secondary selection
              </Button>
            )}
          </div>
        );
      })}
      {choices.length === 1 && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={
            mutation.isPending ||
            preview.candidates.some((candidate) => candidate.key === choices[0]?.policyKey && candidate.selfPay)
          }
          onClick={() => setChoices([...choices, { policyKey: "" }])}
        >
          Add secondary selection
        </Button>
      )}
      <p className="text-sm text-muted-foreground">
        Sync updates Athena where needed and copies the confirmed coverage to this referral. Other chart policies may
        move later in the coverage order. Eligibility and network checks run on the confirmed selection.
      </p>
      {mutation.error && (
        <p role="alert" className="text-sm text-destructive">
          {mutation.error.message}
        </p>
      )}
      <Button type="button" onClick={handleSubmit} disabled={mutation.isPending || incomplete}>
        {mutation.isPending ? "Syncing insurance..." : "Sync and continue"}
      </Button>
    </div>
  );
}

function CoverageResolutionContent({
  taskId,
  mutation,
  onComplete,
}: {
  taskId: string;
  mutation: ReturnType<typeof useResolveAthenaCoverage>;
  onComplete: () => void;
}) {
  const comparison = useAthenaCoverageResolution(taskId, true);

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={comparison.isFetching || mutation.isPending}
        onClick={() => comparison.refetch()}
      >
        Refresh comparison
      </Button>
      {comparison.isPaused ? (
        <p role="alert">Insurance comparison is paused. Check your connection and refresh.</p>
      ) : comparison.isFetching || comparison.isPending ? (
        <p role="status">Loading live insurance...</p>
      ) : comparison.error ? (
        <p role="alert" className="text-sm text-destructive">
          {comparison.error.message || "Could not load insurance. Refresh the comparison to try again."}
        </p>
      ) : (
        <CoverageResolutionForm
          key={comparison.data.version}
          mutation={mutation}
          preview={comparison.data}
          onComplete={onComplete}
        />
      )}
    </>
  );
}

export function AthenaCoverageResolutionDialog({ taskId }: { taskId: string }) {
  const [open, setOpen] = useState(false);
  const mutation = useResolveAthenaCoverage(taskId);
  const invalidateInstructions = useInvalidateServerComponent("getReferralInstructions");

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!mutation.isPending) {
          setOpen(nextOpen);
          mutation.reset();
        }
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm">Continue</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Resolve insurance differences</DialogTitle>
          <DialogDescription>
            Review Valerie and live Athena coverage, then choose the values to sync.
          </DialogDescription>
        </DialogHeader>
        <CoverageResolutionContent
          taskId={taskId}
          mutation={mutation}
          onComplete={() => {
            setOpen(false);
            invalidateInstructions();
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
